"use client";
import Link from "next/link";
import { useFormState, useFormStatus } from "react-dom";
import { signup } from "@/app/[locale]/auth/register";
import { useEffect } from "react";
import toast from "react-hot-toast";
import { useTranslations } from "next-intl";
import AppleSignInButton from "@/components/AppleSingIn";
import MyGoogleSigninButton from "@/components/GoogleSignin";

const SignupPage = () => {
  const t = useTranslations();
  const [state, action] = useFormState(signup, undefined);
  const { pending } = useFormStatus();

  useEffect(() => {
    if (state && state.error) {
      toast.error(state.error);
    }
  }, [state]);

  return (
    <>
      <section className="relative z-10 overflow-hidden pb-16 pt-36 md:pb-20 lg:pb-28 lg:pt-[180px]">
        <div className="container">
          <div className="-mx-4 flex flex-wrap">
            <div className="w-full px-4">
              <div className="shadow-three dark:bg-dark mx-auto max-w-[500px] rounded bg-white px-6 py-10 sm:p-[60px]">
                <h3 className="mb-3 text-center text-2xl font-bold text-black dark:text-white sm:text-3xl">
                  {/*Create your account*/}
                  {t('create_account')}
                </h3>
                {/*<p className="text-body-color mb-11 text-center text-base font-medium">*/}
                {/*  It’s totally free and super easy*/}
                {/*</p>*/}
                <div className="flex justify-center gap-12 pb-5">
                  <MyGoogleSigninButton/>
                  <AppleSignInButton />
                </div>

                <div className="mb-8 flex items-center justify-center">
                  <span className="bg-body-color/50 hidden h-[1px] w-full max-w-[60px] sm:block"></span>
                  <p className="text-body-color w-full px-5 text-center text-base font-medium">
                    {t('or')}
                  </p>
                  <span className="bg-body-color/50 hidden h-[1px] w-full max-w-[60px] sm:block"></span>
                </div>
                <form action={action}>
                  <div className="mb-8">
                    <label
                      htmlFor="name"
                      className="text-dark mb-3 block text-sm dark:text-white"
                    >
                      {t("first_name")}
                    </label>
                    <input
                      type="text"
                      name="firstName"
                      placeholder={t('enter_your_name')}
                      className="border-stroke text-body-color dark:text-body-color-dark dark:shadow-two w-full rounded-sm border bg-[#f8f8f8] px-6 py-3 text-base outline-none transition-all duration-300 focus:border-primary dark:border-transparent dark:bg-[#2C303B] dark:focus:border-primary dark:focus:shadow-none"
                    />
                    {state?.errors?.firstName && (
                      <p className="text-sm text-red-500">
                        {state.errors.firstName}
                      </p>
                    )}
                  </div>
                  <div className="mb-8">
                    <label
                      htmlFor="name"
                      className="text-dark mb-3 block text-sm dark:text-white"
                    >
                      {t("last_name")}
                    </label>
                    <input
                      type="text"
                      name="lastName"
                      placeholder={t('enter_your_last_name')}
                      className="border-stroke text-body-color dark:text-body-color-dark dark:shadow-two w-full rounded-sm border bg-[#f8f8f8] px-6 py-3 text-base outline-none transition-all duration-300 focus:border-primary dark:border-transparent dark:bg-[#2C303B] dark:focus:border-primary dark:focus:shadow-none"
                    />
                    {state?.errors?.lastName && (
                      <p className="text-sm text-red-500">
                        {state.errors.lastName}
                      </p>
                    )}
                  </div>
                  <div className="mb-8">
                    <label
                      htmlFor="email"
                      className="text-dark mb-3 block text-sm dark:text-white"
                    >
                      {t("email")}
                    </label>
                    <input
                      type="email"
                      name="email"
                      placeholder={t('please_enter_an_email')}
                      className="border-stroke text-body-color dark:text-body-color-dark dark:shadow-two w-full rounded-sm border bg-[#f8f8f8] px-6 py-3 text-base outline-none transition-all duration-300 focus:border-primary dark:border-transparent dark:bg-[#2C303B] dark:focus:border-primary dark:focus:shadow-none"
                    />
                    {state?.errors?.email && (
                      <p className="text-sm text-red-500">
                        {state.errors.email}
                      </p>
                    )}
                  </div>
                  <div className="mb-8">
                    <label
                      htmlFor="password"
                      className="text-dark mb-3 block text-sm dark:text-white"
                    >
                      {/*Your Password*/}
                      {t('password')}
                    </label>
                    <input
                      type="password"
                      name="password"
                      placeholder={t('please_enter_a_password')}
                      className="border-stroke text-body-color dark:text-body-color-dark dark:shadow-two w-full rounded-sm border bg-[#f8f8f8] px-6 py-3 text-base outline-none transition-all duration-300 focus:border-primary dark:border-transparent dark:bg-[#2C303B] dark:focus:border-primary dark:focus:shadow-none"
                    />
                    {state?.errors?.password && (
                      <p className="text-sm text-red-500">
                        {state.errors.password}
                      </p>
                    )}
                  </div>
                  <div className="mb-8 flex">
                    <label
                      htmlFor="checkboxLabel"
                      className="text-body-color flex cursor-pointer select-none text-sm font-medium"
                    >
                      <span>
                        {t('privacy1')}
                        <a href="#0" className="text-primary hover:underline ">
                          &nbsp;{t('terms_of_service.termsOfService')}
                        </a>
                        &nbsp;{t('privacy2')}
                        <a href="#0" className="text-primary hover:underline">
                          &nbsp;{t('privacy.privacyPolicy')}
                        </a>
                      </span>
                    </label>
                  </div>
                  <div className="mb-6">
                    <button
                      aria-disabled={pending}
                      type="submit"
                      className="shadow-submit dark:shadow-submit-dark flex w-full items-center justify-center rounded-sm bg-primary px-9 py-4 text-base font-medium text-white duration-300 "
                    >
                      {pending ? "Submitting..." : t("sign_up")}
                    </button>
                  </div>
                </form>
                <p className="text-body-color text-center text-base font-medium">
                  {t('already_have_an_account')}&nbsp;
                  {/*Already using Esimafly?&nbsp;*/}
                  <Link href="/signin" className="text-primary hover:underline">
                    {t("sign_in")}
                  </Link>
                </p>
              </div>
            </div>
          </div>
        </div>
        <div className="absolute left-0 top-0 z-[-1]">
          <svg
            width="1440"
            height="969"
            viewBox="0 0 1440 969"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <mask
              id="mask0_95:1005"
              style={{ maskType: "alpha" }}
              maskUnits="userSpaceOnUse"
              x="0"
              y="0"
              width="1440"
              height="969"
            >
              <rect width="1440" height="969" fill="#090E34" />
            </mask>
            <g mask="url(#mask0_95:1005)">
              <path
                opacity="0.1"
                d="M1086.96 297.978L632.959 554.978L935.625 535.926L1086.96 297.978Z"
                fill="url(#paint0_linear_95:1005)"
              />
              <path
                opacity="0.1"
                d="M1324.5 755.5L1450 687V886.5L1324.5 967.5L-10 288L1324.5 755.5Z"
                fill="url(#paint1_linear_95:1005)"
              />
            </g>
            <defs>
              <linearGradient
                id="paint0_linear_95:1005"
                x1="1178.4"
                y1="151.853"
                x2="780.959"
                y2="453.581"
                gradientUnits="userSpaceOnUse"
              >
                <stop stopColor="#4A6CF7" />
                <stop offset="1" stopColor="#4A6CF7" stopOpacity="0" />
              </linearGradient>
              <linearGradient
                id="paint1_linear_95:1005"
                x1="160.5"
                y1="220"
                x2="1099.45"
                y2="1192.04"
                gradientUnits="userSpaceOnUse"
              >
                <stop stopColor="#4A6CF7" />
                <stop offset="1" stopColor="#4A6CF7" stopOpacity="0" />
              </linearGradient>
            </defs>
          </svg>
        </div>
      </section>
    </>
  );
};

export default SignupPage;
